import React, { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Alert } from 'reactstrap';

import { Button, ErrorResponse, Toolbar } from '~components';
import Icon, { faExclamationTriangle, faPlus } from '~components/icon';
import { Row, TableView } from '~components/table-view';

import columnsFactory from '~support-at-home/forms/excluded-service-types/list';

import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { useGetSettingsQuery } from '~lookups/actions/settings';
import {
  useCreateCompanySettingMutation,
  useGetCompanySettingsQuery,
  useUpdateCompanySettingMutation,
} from '~main/actions/companySettings';

import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';

import ExcludeServiceTypeModal from './ExcludeServiceTypeModal';

interface ComponentProps {
  isActive: boolean;
}

const SETTING_KEY = 'excluded_service_type_ids';

const ExcludedServiceTypes: React.FC<ComponentProps> = ({ isActive }) => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setModalOpen] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const serviceTypes = useAppSelector(selectAppointmentServiceTypes);

  const {
    data: lookupSettings = [],
    isLoading: isLoadingSettings,
    error: settingsError,
  } = useGetSettingsQuery({ feature_tag: 'sah' }, { skip: !isActive });
  const {
    data: companySettings = [],
    isLoading: isLoadingCompanySettings,
    error: companySettingsError,
  } = useGetCompanySettingsQuery(undefined, { skip: !isActive });

  const [createSetting, { isLoading: isCreating }] = useCreateCompanySettingMutation();
  const [updateSetting, { isLoading: isUpdating }] = useUpdateCompanySettingMutation();

  useEffect(() => {
    if (isActive && serviceTypes.length === 0) {
      dispatch(getServiceTypes());
    }
  }, [isActive, dispatch, serviceTypes.length]);

  const companySetting = useMemo(() => companySettings.find((s) => s.setting?.key === SETTING_KEY), [companySettings]);

  const excludedIds: number[] = useMemo(() => {
    if (!companySetting?.value) return [];
    try {
      const parsed = JSON.parse(companySetting.value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [companySetting]);

  const excludedServiceTypes = useMemo(() => {
    return serviceTypes.filter((s) => excludedIds.includes(s.id)).map((s) => ({ id: s.id, name: s.name }));
  }, [serviceTypes, excludedIds]);

  const saveExclusions = async (ids: number[]) => {
    const value = JSON.stringify(ids);
    setMutationError(null);
    if (!companySetting) {
      const lookupSettingId = lookupSettings.find((s) => s.key === SETTING_KEY)?.id;
      if (!lookupSettingId) {
        setMutationError('Unable to save: the setting for service type exclusions was not found.');
        return;
      }
      await createSetting({ lookup_setting_id: lookupSettingId, value }).unwrap();
    } else {
      await updateSetting({ id: companySetting.id, value }).unwrap();
    }
  };

  const handleExclude = async (newIds: number[]) => {
    try {
      const merged = [...new Set([...excludedIds, ...newIds])];
      await saveExclusions(merged);
      setModalOpen(false);
    } catch (error) {
      setMutationError(error instanceof Error ? error.message : 'Failed to save exclusions');
    }
  };

  const handleRemove = async (row: Row) => {
    try {
      const updated = excludedIds.filter((id) => id !== row.id);
      await saveExclusions(updated);
    } catch (error) {
      setMutationError(error instanceof Error ? error.message : 'Failed to remove exclusion');
    }
  };

  const isLoading = isLoadingSettings || isLoadingCompanySettings;
  const errorMessage = [settingsError, companySettingsError, mutationError].filter(Boolean).map(String).join(' ');

  const columns = columnsFactory(handleRemove);

  return (
    <div className="p-2">
      <ErrorResponse message={errorMessage} dataCy="excluded-service-types-error" />
      <Toolbar
        id="excluded-service-types"
        rightCluster={
          <Button id="sah-exclude-service-type-button" size="sm" color="success" onClick={() => setModalOpen(true)}>
            <Icon className="me-2" icon={faPlus} />
            Exclude Service Type
          </Button>
        }
      />

      <Alert color="warning" className="d-flex align-items-start gap-2 mt-2">
        <Icon icon={faExclamationTriangle} className="mt-1" />
        <div>
          <strong>Warning:</strong> These service types are excluded from <strong>ALL</strong> SaH features including
          Claims, Budgets, Supplier Invoices, Price Plans, and Appointment Imports. This is a blanket restriction that
          affects all clients.
        </div>
      </Alert>

      <TableView
        id="excluded-service-types"
        columns={columns}
        data={excludedServiceTypes as Row[]}
        isLoading={isLoading}
        readonly
        striped
        emptyMessage="No service types are currently excluded"
      />

      <ExcludeServiceTypeModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleExclude}
        currentExclusions={excludedIds}
        serviceTypes={serviceTypes}
        isSaving={isCreating || isUpdating}
      />
    </div>
  );
};

export default ExcludedServiceTypes;
